import { ArrowRight } from 'lucide-react';
import Image from 'next/image';
import React from 'react';
import { Card, CardContent, CardFooter } from '@/app/_components/core/card';
import { SmartLink } from '@/app/_components/core/smart-link';
import { CardSectionElement } from '@/app/_components/shared/card-section/card-section.types';
import { cn, emptyToUndefined } from '@/app/_lib/utils';

interface IconCardProps {
  card: CardSectionElement;
  className?: string;
  trackId?: string;
}

// Design token classes
const cardTokenClasses = [
  // Radius
  'rounded-icon-card',
  // Padding (mobile)
  'pt-icon-card-pt pb-icon-card-pb pl-icon-card-pl pr-icon-card-pr',
  // Padding (desktop)
  'lg:pt-icon-card-pt-lg lg:pb-icon-card-pb-lg lg:pl-icon-card-pl-lg lg:pr-icon-card-pr-lg',
  // Gap
  'gap-icon-card xl:gap-icon-card-lg',
].join(' ');

// Standard classes
const cardBaseClasses =
  'group relative flex flex-col justify-between overflow-hidden border-none bg-accent shadow-none transition-colors';

const linkOverlayClasses =
  'absolute inset-0 z-10 rounded-icon-card focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:outline-none';

const arrowButtonClasses =
  'flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm transition-transform duration-300 [@media(hover:hover)]:group-hover:scale-105';

export function IconCard({ card, className, trackId }: IconCardProps) {
  const cta = card.cta;
  const isLinked = !!cta?.linkUrl;

  return (
    <Card className={cn(cardTokenClasses, cardBaseClasses, className)}>
      <CardContent className="flex-1 px-0">
        {card.icon && (
          <Image
            src={card.icon.url}
            alt={card.icon.altText ?? ''}
            className={cn(
              'size-42  transition-transform duration-300',
              isLinked && '[@media(hover:hover)]:group-hover:scale-105'
            )}
            width={168}
            height={168}
          />
        )}
      </CardContent>

      <CardFooter className="justify-between gap-4 px-0">
        {card.title && (
          <h3 className="line-clamp-3 max-w-7/10 text-3xl font-medium leading-none text-primary wrap-break-word lg:text-4xl">
            {card.title}
          </h3>
        )}

        {isLinked && (
          <div className={arrowButtonClasses}>
            <ArrowRight className="size-6 " />
          </div>
        )}
      </CardFooter>

      {isLinked && (
        <SmartLink
          href={cta.linkUrl}
          target={cta.linkTarget}
          download={cta.download}
          className={linkOverlayClasses}
          aria-label={emptyToUndefined(card.title) ?? cta.linkTitle}
          data-track-id={trackId}
        />
      )}
    </Card>
  );
}
